import React, { useState } from 'react';
import { cn } from '@/shared/utils/shadcn';
import QAItem from '../QAItem';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';

const QA = ({ className }: { className?: string }) => {
  const t = useTranslations();
  const currentLocale = useLocale();
  const [showAll, setShowAll] = useState<boolean>(false); // 查看更多
  const qaList = [
    {
      question: '在美国境内申请EB-3，和在境外领馆面签有什么区别？',
      answer:
        '对于符合条件的申请人，如果人在美国境内，可以直接递交 I-485（身份调整，Adjustment of Status），无需经历领馆面试。相比之下，这通常比在境外申请移民签证更快，一般能节省大约6个月，有时甚至超过半年。原因在于：境内只需等待 USCIS 审理和采集指纹，而境外则需要排队等待面签名额。在某些时期，领馆面签的积压会拉长时间差。',
    },
    {
      question: '中国大陆申请者的EB-3排期情况如何？',
      answer:
        '每年10月1日，美国国务院都会释放新一财年的签证名额。如果某个类别和出生国的申请量较少，排期就会推进得更快。以数据为例，2023财年中国大陆出生的EB-3非技术申请仅约49份，数量极低。这意味着未来几年排期有望保持快速前进，预计中国大陆申请人极大可能在五年之内排期可见。',
    },
    {
      question: 'EB-3排期是什么意思？哪些因素会影响排期长短？',
      specialAnswer: (
        <div>
          <p>
            EB-3移民的排期是指申请人从提交申请到获得绿卡之间所需等待的时间。由于每年移民签证的数量有限，申请人需要根据其“优先日期”（Priority
            Date）和美国国务院每月发布的《签证公告》（Visa Bulletin）中的排期日期，来确定何时可以继续申请流程。
          </p>
          <br />
          <div>
            <div>这些因素都有可能影响排期的长短：</div>
            <p>1、签证配额限制：每个国家每年的职业移民签证配额约为总数的7%；</p>
            <p>2、申请人数：高申请量国家（包括中国、印度和菲律宾）的申请人可能需要等待更长时间；</p>
            <p>3、签证类别：EB-3类别下的“其他工人”子类别的排期比“技术工人”和“专业人员”相对更长</p>
          </div>
          <br />
          <p>注意：申请人夫妻任意一方如果出生国不需要排期，则可以免除排期要求。</p>
        </div>
      ),
    },
    {
      question: '在申请EB-3签证期间，我是否可以更换雇主？',
      answer:
        '在申请EB-3签证期间，如果不得已，比如雇主破产等，可以更换雇主，前提是新的雇主符合要求，并愿意担保支持移民。值得一提的是，EB-3项目的排期日是从第一个雇主劳工证递交当日开始计算，即使后续不得不更换雇主，排期日也不会发生变化，不需要重新开始计算，只需要重新申请劳工证和递交移民申请。在登陆前变更雇主，需重新申请 PERM & I-140，会导致约6-12个月的额外审查时间，一定程度延缓申请进度。',
    },
    {
      question: '作为中国申请人，多久能拿到绿卡？',
      specialAnswer: (
        <div>
          <p>
            截至目前（2025年5月），EB-3 Other
            Workers（非技术类）中国大陆出生的排期为2017年8月1日，表面上看似需要等待8年左右。但EB-3的推进并非线性渐进式的，而是跳跃式的。
          </p>
          <p>
            受到疫情、护照申请限制和国内知名度不高的因素影响，2017年至今以来的中国大陆申请人数锐减，因此在接下来的审批排期可能会推进迅速、甚至跳跃式推进，等待时间可能远少于8年，预期会在4到6年左右。
          </p>
        </div>
      ),
    },
    {
      question: '我学历不高，英语不够流利，能够申请该项目吗？',
      answer: '可以！EB-3非技术类无学历和语言要求，也没有相在工作经验的要求，是一个低门槛移民项目。',
    },
    {
      question: '我是否可以在办理EB-3签证期间，办理其他美国移民签证项目？',
      answer:
        '可以，美国移民项目通常没有排他性，留学生在办理H1B工签，企业家在办理EB5，文体人才在办理EB1a等项目期间，如果担心后续移民出现问题，只要符合要求，都可以同时申请EB-3.',
    },
    {
      question: '我的家人可以随我一起移民吗？多大的孩子可以跟我一起移民？',
      specialAnswer: (
        <div>
          <p>
            可以。您的配偶和未满21岁的未婚子女可以作为附属申请人（Derivative
            Beneficiaries）随您一起申请绿卡。他们需要分别提交相关申请，并满足体检等要求。
            需要注意，这里的“21岁”年龄是指“子女在签证可用时的实际年龄”减去I-140申请的审理时间（以天数计）。{' '}
          </p>
          <p>
            例如，如果子女在签证可用时的实际年龄为21岁零5个月，而I-140申请的审理时间为6个月，则：21岁零5个月 - 6个月 =
            20岁零11个月。在此情况下，子女仍属于年龄未满21岁，符合作为附属申请人申请绿卡的条件。{' '}
          </p>
        </div>
      ),
    },
    {
      question: 'EB-3的获签批准率高吗？',
      answer:
        '根据USCIS的数据，EB-3类别的I-140申请批准率通常在90%左右，是美国移民项目中成功率最高的项目之一。成功的关键在于雇主的合规性、申请人的资格以及提交的文件是否完整准确。',
    },
    {
      question: 'EB-3需要面试吗？会被问什么问题？',
      specialAnswer: (
        <div>
          <div>是的，在领事处理阶段，EB-3申请人需要参加签证面试。常见问题包括但不限于：</div>
          <div>- 您在美国的工作职责是什么？</div>
          <div>- 您在中国的工作经历和教育背景。</div>
          <div>- 您是否有犯罪记录或违反移民法的历史。</div>
          <div>- 您是否有足够的资金支持自己在美国的生活。</div>
          <div>建议您提前准备相关文件，诚实回答所有问题。星环也会为客户提供面试指导，帮助客户顺利通过面试。</div>
        </div>
      ),
    },
    {
      question: 'EB-3的排期是如何计算的？',
      specialAnswer: (
        <div>
          <div>
            对于大多数EB-3申请者，排期计算起点是优先日期，它是您在EB-3移民申请队列中的位置，决定了您何时可以继续申请绿卡。通常情况下：优先日期是雇主向美国劳工部（DOL）提交PERM劳工认证申请的日期。如果不需要劳工认证：则为雇主向美国移民局（USCIS）提交I-140移民申请的日期。
          </div>
          <div>
            如果您要查看排期进度，可以查询美国国务院每月发布《签证公告》（Visa Bulletin），其中包含两个关键日期：
          </div>
          <div>1、最终裁定日期（Final Action Date）：当您的优先日期早于该日期时，表示可以进行绿卡申请的最后步骤。</div>
          <div>
            2、递交申请日期（Dates for Filing）：当您的优先日期早于该日期时，表示可以提前准备和提交相关申请材料。
            您需要将您的优先日期与签证公告中的相应日期进行比较，以确定何时可以提交I-485调整身份申请或进行领事处理。
          </div>
        </div>
      ),
    },
    {
      question: '我是否可以在没有美国offer的情况下申请EB-3签证？',
      answer:
        '不能，EB-3签证要求提供美国雇主的工作offer，并获得劳工证批准。美国雇主必须证明在该职位上找不到合格的美国工人。',
    },
    {
      question: 'EB-3签证是否有最低工资要求？',
      answer:
        '虽然EB-3签证没有固定的最低工资要求，但雇主必须提供符合特定地理区域职业的现行工资水平的工资。这个现行工资由美国劳工部确定。',
    },
    {
      question: '每年EB-3签证的发放数量是否有限制？',
      answer:
        '是的，与大多数其他基于就业的签证类别一样，EB-3签证类别每年都有发放签证数量的限制。这个限制由美国政府确定，根据签证需求和原籍国等因素而有所不同。',
    },
    {
      question: '我是否可以为EB-3签证申请启用加急服务？',
      answer:
        '加急服务是针对EB-3签证类别中的I-140表格而提供的，通常可以加速到15个工作日完成加急审理。然而，快速处理服务对于I-485表格或领事馆处理不可用。',
    },
    {
      question: '我是否可以在持有非移民签证的情况下，在美国境内申请EB-3签证？',
      answer:
        '是的，您可以在持有有效非移民签证的情况下在美国境内申请EB-3签证。如果符合条件，您可以提交I-485表格将您的身份调整为永久居民，而无需离开美国。',
    },
    {
      question: '什么是PERM？它需要花多长时间批准？',
      specialAnswer: (
        <div>
          <div>
            PERM（Program Electronic Review
            Management）是美国劳工部（DOL）实施的永久劳工认证程序，旨在确保雇佣外籍员工不会对美国本地工人的就业机会、工资和工作条件产生不利影响。PERM是整个EB-3申请的起点。只有美国劳工部认定您的雇主无法在美国招到合适的员工，才会为雇主发放PERM，允许它为您担保前来美国工作。
          </div>
          <div>
            整个PERM申请流程通常需要数月时间，具体取决于各个阶段的处理速度和是否被选中审计。一旦PERM申请获得批准，雇主需在180天内为外籍员工提交I-140移民申请。
          </div>
          <div>
            需要注意的是：PERM申请必须由雇主发起，外籍员工不能自行申请；职位的资格要求应符合行业标准，避免设置过高门槛，以免被认为是排除美国申请人的手段。你需要和雇主沟通并确保以上事项。
          </div>
        </div>
      ),
    },
    {
      question: '我可以在美国境内申请EB-3移民吗？',
      answer:
        '如果您已经在美国，并持有有效的非移民身份，且签证排期已到，您可以提交I-485申请进行身份调整。此过程通常需要8至14个月，具体时间取决于USCIS的处理速度。 ',
    },
    {
      question: '登陆之后我可以更换工作吗？',
      answer:
        '由于这是一个雇主担保类移民项目，雇主支持申请人移民的前提是满足其工作要求。通常应聘者适应工作需要一定时间，这是正常的。如果实在难以适应，建议工作满12个月再考虑离职，避免移民局质疑移民动机不纯。',
    },
    {
      question: '获得绿卡后，我们能享受哪些福利？？',
      specialAnswer: (
        <div>
          <div>获得绿卡之后，您和您的家庭可以享受以下福利：</div>
          <br />
          <table className="w-full table-auto border-collapse border border-gray-300">
            <colgroup>
              <col style={{ width: '14.2857%' }} />
              <col style={{ width: '28.5714%' }} />
              <col style={{ width: '57.1429%' }} />
            </colgroup>
            <thead>
              <tr>
                <th className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">福利类别</th>
                <th className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">具体权益</th>
                <th className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">说明</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">子女教育</td>
                <td className="border border-gray-300 px-4 py-2">免费公立教育</td>
                <td className="border border-gray-300 px-4 py-2">
                  绿卡持有者的子女有权在美国接受从幼儿园到高中的免费公立教育。
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2"></td>
                <td className="border border-gray-300 px-4 py-2">州内大学学费优惠</td>
                <td className="border border-gray-300 px-4 py-2">
                  在满足居住要求后，子女可享受州内大学的学费优惠（in-state tuition），通常比国际学生或非居民的学费低。
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">配偶就业</td>
                <td className="border border-gray-300 px-4 py-2">合法工作权利</td>
                <td className="border border-gray-300 px-4 py-2">
                  配偶在获得绿卡后，即成为永久居民，有权在美国合法工作，无需额外的工作许可。
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">医疗保险</td>
                <td className="border border-gray-300 px-4 py-2">购买私人医疗保险</td>
                <td className="border border-gray-300 px-4 py-2">作为永久居民，您和家人可以购买私人医疗保险。</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2"></td>
                <td className="border border-gray-300 px-4 py-2">通过《平价医疗法案》（ACA）获得医疗保险</td>
                <td className="border border-gray-300 px-4 py-2">
                  绿卡持有者有资格通过ACA获得医疗保险，具体取决于收入和居住时间等因素。
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">社会保障</td>
                <td className="border border-gray-300 px-4 py-2">参与社会保障系统</td>
                <td className="border border-gray-300 px-4 py-2">
                  作为永久居民，您和家人有资格参与社会保障（Social
                  Security）系统，为未来的退休、残疾或其他情况提供保障。
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ),
    },
  ];

  const qaListEn = [
    {
      question: 'My child is studying in the U.S., and I am a parent accompanying them. Am I eligible for EB-3?',
      answer:
        'If your child is attending primary, secondary, or undergraduate school in the U.S., and you are traveling back and forth on a B visa, your status is unstable and the cost of living is high. EB-3 provides a comprehensive solution of "legal status + U.S. employment + family reunification + U.S. benefits" for the entire family. Once approved, the whole family can obtain green cards. As a U.S. resident, your child can enjoy tuition discounts, freely choose university majors as a domestic student, and benefit from a higher admission rate to prestigious universities. Parents can also work and live in the U.S. more freely and flexibly.',
    },
    {
      question: 'I am an international student in the U.S. Am I eligible for EB-3?',
      answer:
        'In recent years, the U.S. has become increasingly strict with international students. Even if you get a work visa after graduation, obtaining a green card ultimately depends on luck and is very difficult. As a student, if you want a more secure path to immigration, you can both try to get a quota and use EB-3 as a Plan B to lock in an immigration slot. EB-3 acts as a backup plan, avoiding the risk of immigration failure due to factors like not getting a work visa, unemployment, or employer refusal to sponsor.',
    },
    {
      question: 'What is the EB-3 visa backlog? What factors affect how long it takes?',
      specialAnswer: (
        <div>
          <p>
            {`The EB-3 visa backlog refers to the waiting period between submitting your application and receiving your
            green card. Due to annual visa limits, applicants must refer to their "Priority Date" and the monthly Visa
            Bulletin issued by the U.S. Department of State to determine when they can proceed with their application.`}
          </p>
          <br />
          <div>
            <div>These factors can affect the length of the backlog:</div>
            <p>1. Visa quota limits: Each country gets about 7% of the total employment-based visas per year;</p>
            <p>
              2. Number of applicants: Applicants from high-volume countries (including China, India, and the
              Philippines) may wait longer;
            </p>
            <p>
              3. Visa category: The “Other Workers” subcategory under EB-3 usually has a longer wait time than “Skilled
              Workers” or “Professionals”.
            </p>
          </div>
          <br />
          <p>
            Note: If either spouse was born in a country that doesn’t have a backlog, the priority date requirement can
            be waived.
          </p>
        </div>
      ),
    },
    {
      question: 'Can I change employers during the EB-3 application process?',
      answer:
        'Yes, if absolutely necessary (e.g., the employer goes bankrupt), you can change employers, provided the new employer meets the requirements and is willing to support your application. Notably, the priority date from the first PERM filing will remain valid even if you change employers later. You will only need to file a new PERM and I-140, and the change may add 6–12 months of additional processing time before entry.',
    },
    {
      question: 'As a Chinese applicant, how long does it take to get a green card?',
      specialAnswer: (
        <div>
          <p>
            As of May 2025, the priority date for EB-3 Other Workers (unskilled) born in mainland China is August 1,
            2017, which seems to imply an 8-year wait. However, EB-3 backlog movement is not linear but jumpy.
          </p>
          <p>
            Due to factors like COVID-19, passport delays, and low public awareness, the number of Chinese applicants
            has sharply dropped since 2017. As a result, the backlog may advance quickly or even jump forward, and the
            actual wait may be much shorter than 8 years—potentially 4 to 6 years.
          </p>
        </div>
      ),
    },
    {
      question: 'My education is low, and my English is not fluent. Can I still apply for this program?',
      answer:
        'Yes! The EB-3 Other Workers category does not require any education, language skills, or relevant work experience. It is a low-barrier immigration program.',
    },
    {
      question: 'Can I apply for another U.S. immigration program while applying for EB-3?',
      answer:
        'Yes, U.S. immigration programs are usually not exclusive. Students applying for H1B, entrepreneurs applying for EB-5, or talent-based applicants applying for EB-1a can still apply for EB-3 as a backup, as long as they meet the requirements.',
    },
    {
      question: 'Can my family immigrate with me? What age children can be included?',
      specialAnswer: (
        <div>
          <p>
            Yes. Your spouse and unmarried children under the age of 21 can immigrate with you as derivative
            beneficiaries. They must file separate applications and meet medical exam and other requirements. Note that
            the age “under 21” is calculated as the actual age at the time the visa becomes available, minus the time
            the I-140 was under review (in days).
          </p>
          <p>
            For example, if your child is 21 years and 5 months old when the visa becomes available, and the I-140
            review took 6 months, then: 21 years 5 months - 6 months = 20 years 11 months. In this case, the child still
            qualifies as “under 21” and is eligible to apply as a derivative beneficiary.
          </p>
        </div>
      ),
    },
    {
      question: 'Is the approval rate for EB-3 high?',
      answer:
        'According to USCIS data, the approval rate for I-140 under EB-3 is typically around 90%, making it one of the highest success rate immigration categories. Success mainly depends on the employer’s compliance, the applicant’s qualifications, and whether the documents are complete and accurate.',
    },
    {
      question: 'Does EB-3 require an interview? What questions will be asked?',
      specialAnswer: (
        <div>
          <div>
            Yes, EB-3 applicants must attend a visa interview during consular processing. Common questions include:
          </div>
          <div>- What are your job responsibilities in the U.S.?</div>
          <div>- Your work experience and education background in China.</div>
          <div>- Any criminal record or immigration violations.</div>
          <div>- Whether you have sufficient funds to support yourself in the U.S.</div>
          <div>
            We recommend preparing relevant documents in advance and answering honestly. Our team will also provide
            interview coaching to help you succeed.
          </div>
        </div>
      ),
    },
    {
      question: 'How is the EB-3 priority date calculated?',
      specialAnswer: (
        <div>
          <div>
            {`For most EB-3 applicants, the priority date marks your place in line and determines when you can move
            forward with your green card application. Normally, the priority date is the date your employer files the
            PERM application with the Department of Labor. If no PERM is required, then it's the date the employer files
            Form I-140 with USCIS.`}
          </div>
          <div>
            To check your backlog progress, refer to the monthly Visa Bulletin from the U.S. Department of State. It
            includes two key dates:
          </div>
          <div>
            1. Final Action Date: If your priority date is earlier than this, you can proceed with the final step of the
            green card process.
          </div>
          <div>
            2. Dates for Filing: If your priority date is earlier than this, you can prepare and submit your green card
            application documents in advance. Compare your priority date with these dates to determine when to submit
            I-485 or attend your consular interview.
          </div>
        </div>
      ),
    },
    {
      question: 'Can I apply for EB-3 without a U.S. job offer?',
      answer:
        'No. EB-3 requires a valid job offer from a U.S. employer and approval of a labor certification. The employer must prove that no qualified U.S. workers are available for the position.',
    },
    {
      question: 'Does the EB-3 visa have a minimum wage requirement?',
      answer:
        'While EB-3 does not have a fixed minimum wage, the employer must offer a wage that meets the prevailing wage for the occupation and geographic area, as determined by the U.S. Department of Labor.',
    },
    {
      question: 'Is there a limit on the number of EB-3 visas issued each year?',
      answer:
        'Yes, like most employment-based visa categories, EB-3 has annual limits. These are determined by the U.S. government and vary based on demand and the applicant’s country of origin.',
    },
    {
      question: 'Can I request premium processing for my EB-3 application?',
      answer:
        'Premium processing is available for Form I-140 under EB-3 and usually results in processing within 15 business days. However, this service is not available for Form I-485 or consular processing.',
    },
    {
      question: 'Can I apply for EB-3 from within the U.S. while holding a non-immigrant visa?',
      answer:
        'Yes, if you are legally in the U.S. on a valid non-immigrant visa and your priority date is current, you may file Form I-485 to adjust status to permanent resident without leaving the country.',
    },
    {
      question: 'What is PERM? How long does it take to get approved?',
      specialAnswer: (
        <div>
          <div>
            PERM (Program Electronic Review Management) is a permanent labor certification process run by the U.S.
            Department of Labor (DOL). It ensures that hiring a foreign worker will not negatively impact U.S. workers’
            job opportunities, wages, or working conditions. PERM is the starting point of the EB-3 process. Only if the
            DOL certifies the employer’s inability to find suitable U.S. workers, the employer may sponsor the foreign
            employee.
          </div>
          <div>
            {`The PERM process usually takes several months, depending on each step's processing time and whether the case
            is selected for audit. Once approved, the employer must file the I-140 within 180 days.`}
          </div>
          <div>
            Important notes: PERM must be initiated by the employer. Foreign workers cannot apply on their own. Job
            requirements must meet industry norms and avoid excessive barriers that may exclude U.S. workers. You should
            communicate with your employer to ensure these conditions are met.
          </div>
        </div>
      ),
    },
    {
      question: 'Can I apply for EB-3 while inside the U.S.?',
      answer:
        'If you are already in the U.S. on a valid non-immigrant visa and your priority date is current, you can submit Form I-485 to adjust your status. This process usually takes 8 to 14 months depending on USCIS processing times.',
    },
    {
      question: 'Can I change jobs after receiving my green card?',
      answer: `Since this is an employer-sponsored immigration program, the employer expects you to fulfill the job requirements. A reasonable transition period is expected. If you find it truly difficult to adjust, it's generally recommended to wait at least 12 months before switching jobs to avoid suspicion from USCIS regarding your immigration intent.`,
    },
    {
      question: 'What benefits can we enjoy after getting a green card?',
      specialAnswer: (
        <div>
          <div>After receiving your green card, you and your family can enjoy the following benefits:</div>
          <br />
          <table className="w-full table-auto border-collapse border border-gray-300">
            <colgroup>
              <col style={{ width: '14.2857%' }} />
              <col style={{ width: '28.5714%' }} />
              <col style={{ width: '57.1429%' }} />
            </colgroup>
            <thead>
              <tr>
                <th className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">Benefit Category</th>
                <th className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">Specific Right</th>
                <th className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">Child Education</td>
                <td className="border border-gray-300 px-4 py-2">Free public education</td>
                <td className="border border-gray-300 px-4 py-2">
                  Children of green card holders are entitled to free public education from kindergarten through high
                  school in the U.S.
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2"></td>
                <td className="border border-gray-300 px-4 py-2">In-state tuition discount</td>
                <td className="border border-gray-300 px-4 py-2">
                  After meeting residency requirements, children can enjoy lower in-state tuition fees at state
                  universities, which are typically lower than international or non-resident rates.
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">Spouse Employment</td>
                <td className="border border-gray-300 px-4 py-2">Legal work rights</td>
                <td className="border border-gray-300 px-4 py-2">
                  Once granted a green card, spouses can legally work in the U.S. without requiring additional work
                  permits.
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">Health Insurance</td>
                <td className="border border-gray-300 px-4 py-2">Purchase private insurance</td>
                <td className="border border-gray-300 px-4 py-2">
                  As permanent residents, you and your family can purchase private health insurance in the U.S.
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2"></td>
                <td className="border border-gray-300 px-4 py-2">ACA-based insurance</td>
                <td className="border border-gray-300 px-4 py-2">
                  Green card holders may qualify for insurance through the Affordable Care Act (ACA), depending on
                  income and residency duration.
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold text-[#333333]">Social Security</td>
                <td className="border border-gray-300 px-4 py-2">Participate in Social Security</td>
                <td className="border border-gray-300 px-4 py-2">
                  As permanent residents, you and your family may participate in the Social Security system, which
                  provides support for retirement, disability, and other circumstances.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ),
    },
  ];

  const fullList = currentLocale === 'en' ? qaListEn : qaList;
  const displayedList = showAll ? fullList : fullList.slice(0, 8);

  return (
    <div className={cn('w-full', className)}>
      <div className="mb-[1.456vw] box-border px-[0.52vw] text-[1.716vw] font-semibold leading-[1.716vw] text-[#191148]">
        Q&A
      </div>
      {displayedList.map((item, index) => (
        <QAItem
          className="mb-[1.04vw]"
          key={index}
          question={item.question}
          answer={item.answer}
          specialAnswer={item.specialAnswer}
          index={index}
        />
      ))}
      {/* 显示全部按钮 */}
      {!showAll && (
        <div className="mt-[1.56vw] flex w-full flex-row items-center justify-center">
          <div
            className="flex cursor-pointer flex-row items-center justify-center rounded-full border-2 border-solid border-[#2A2B45] px-[1.04vw] py-[0.728vw]"
            onClick={() => setShowAll(!showAll)}
          >
            <div className="mr-[0.728vw] text-[0.884vw] leading-none text-[#2A2B45]">{t('Landing.showAll')}</div>
            <Image
              src={'/images/common/arrow.png'}
              width={15}
              height={9}
              className={`user-select-none h-auto w-[0.78vw] cursor-pointer transition-all max-md:w-2 ${showAll ? 'rotate-0' : 'rotate-180'}`}
              alt="search"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default QA;
